import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Zap, ArrowLeft, Loader2, Plus, Users, Link as LinkIcon, Copy, Check,
  CircleDollarSign, Eye, Pause, Play, Trash2, Download, X, FileText,
  ExternalLink, Settings, CheckCircle,
} from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axiosAuth = () => axios.create({
  withCredentials: true,
  headers: { Authorization: `Bearer ${localStorage.getItem("lf_session") || ""}` },
});

const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function CreatePromoterModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", code: "", commission_rate: "0.15", notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.name.trim() || !form.email.includes("@")) { setErr("Valid name + email required"); return; }
    setSaving(true);
    try {
      const body = { name: form.name, email: form.email };
      if (form.code.trim()) body.code = form.code.trim();
      if (form.commission_rate) body.commission_rate = parseFloat(form.commission_rate);
      if (form.notes.trim()) body.notes = form.notes.trim();
      const res = await axiosAuth().post(`${API}/admin/promoters`, body);
      onCreated?.(res.data); onClose();
    } catch (e) { setErr(e.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4" onClick={onClose} data-testid="promoter-create-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-bold text-zinc-950">Add a promoter</h3>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-lg text-zinc-500"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input required data-testid="input-promoter-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Full name" className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
          <input required type="email" data-testid="input-promoter-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="email@example.com" className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-zinc-600">Tracking code (auto if blank)</label>
              <input data-testid="input-promoter-code" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
                placeholder="jane-xyz" className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            </div>
            <div>
              <label className="text-xs font-semibold text-zinc-600">Commission rate (0.15 = 15%)</label>
              <input data-testid="input-promoter-rate" type="number" step="0.01" min="0" max="1" value={form.commission_rate}
                onChange={e => setForm({ ...form, commission_rate: e.target.value })}
                className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            </div>
          </div>
          <textarea data-testid="input-promoter-notes" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Internal notes (optional)"
            className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
          {err && <div data-testid="promoter-create-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
          <button type="submit" disabled={saving} data-testid="btn-create-promoter"
            className="w-full py-3 bg-zinc-950 text-white rounded-full font-semibold text-sm hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : "Create promoter"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PromoterRow({ p, onUpdated, origin }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [savingPayout, setSavingPayout] = useState(false);

  const link = `${origin}/?ref=${p.code}`;

  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const toggleStatus = async () => {
    const next = p.status === "active" ? "suspended" : "active";
    try { await axiosAuth().patch(`${API}/admin/promoters/${p.promoter_id}`, { status: next }); onUpdated?.(); } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const loadDetail = async () => {
    if (!expanded) {
      const [resD, resT] = await Promise.all([
        axiosAuth().get(`${API}/admin/promoters/${p.promoter_id}`),
        axiosAuth().get(`${API}/admin/promoters/${p.promoter_id}/traffic?days=30`),
      ]);
      setDetail(resD.data);
      setTraffic(resT.data);
    }
    setExpanded(!expanded);
  };

  const payoutEligible = (detail?.commissions || []).filter(c => c.status === "pending");
  const payoutAmount = payoutEligible.reduce((s, c) => s + (c.amount_usd || 0), 0);

  const recordPayout = async () => {
    if (!payoutEligible.length) return;
    const reference = window.prompt(`Record payout of ${fmt(payoutAmount)} — paste a reference (wire ID, PayPal receipt, etc.):`, "");
    if (reference === null) return;
    setSavingPayout(true);
    try {
      await axiosAuth().post(`${API}/admin/promoters/${p.promoter_id}/payouts`, {
        commission_ids: payoutEligible.map(c => c.commission_id),
        method: "manual", reference: reference || undefined,
      });
      onUpdated?.();
      const res = await axiosAuth().get(`${API}/admin/promoters/${p.promoter_id}`);
      setDetail(res.data);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setSavingPayout(false); }
  };

  return (
    <>
      <tr data-testid={`promoter-row-${p.promoter_id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
        <td className="px-5 py-3">
          <div className="font-semibold text-zinc-900 text-sm">{p.name}</div>
          <div className="text-xs text-zinc-500">{p.email}</div>
        </td>
        <td className="px-5 py-3">
          <div className="inline-flex items-center gap-2 bg-zinc-100 rounded-full px-3 py-1 text-xs font-mono">
            <span>/?ref={p.code}</span>
            <button data-testid={`btn-copy-link-${p.promoter_id}`} onClick={copy} className="text-zinc-600 hover:text-zinc-900">
              {copied ? <Check size={11} className="text-emerald-600" /> : <Copy size={11} />}
            </button>
          </div>
        </td>
        <td className="px-5 py-3 text-zinc-700 text-sm">{Math.round((p.commission_rate || 0.15) * 100)}%</td>
        <td className="px-5 py-3 text-sm text-center" data-testid={`promoter-clicks-${p.promoter_id}`}>{p.stats?.clicks || 0}</td>
        <td className="px-5 py-3 text-sm text-center">{p.stats?.attributed_projects || 0}</td>
        <td className="px-5 py-3 text-right">
          <div className="text-sm font-bold text-emerald-700" data-testid={`promoter-pending-${p.promoter_id}`}>{fmt(p.stats?.pending_commission_usd)}</div>
          <div className="text-[10px] text-zinc-400">pending</div>
        </td>
        <td className="px-5 py-3">
          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{p.status}</span>
        </td>
        <td className="px-5 py-3">
          <div className="flex gap-1">
            <button onClick={loadDetail} title="Details" className="p-1.5 text-zinc-600 hover:bg-zinc-100 rounded-lg" data-testid={`btn-details-${p.promoter_id}`}>
              <Eye size={13} />
            </button>
            <button onClick={toggleStatus} title={p.status === "active" ? "Suspend" : "Reactivate"} className="p-1.5 text-zinc-600 hover:bg-zinc-100 rounded-lg" data-testid={`btn-toggle-${p.promoter_id}`}>
              {p.status === "active" ? <Pause size={13} /> : <Play size={13} />}
            </button>
          </div>
        </td>
      </tr>
      {expanded && detail && (
        <tr className="bg-zinc-50/60">
          <td colSpan={8} className="px-5 py-5" data-testid={`promoter-detail-${p.promoter_id}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
              <div className="bg-white rounded-xl border border-zinc-200 p-3">
                <div className="text-xs text-zinc-500">Total clicks</div>
                <div className="text-xl font-extrabold text-zinc-950">{detail.stats.clicks}</div>
              </div>
              <div className="bg-white rounded-xl border border-zinc-200 p-3">
                <div className="text-xs text-zinc-500">Attributed / delivered</div>
                <div className="text-xl font-extrabold text-zinc-950">{detail.stats.attributed_projects} / {detail.stats.paid_projects}</div>
              </div>
              <div className="bg-white rounded-xl border border-zinc-200 p-3">
                <div className="text-xs text-zinc-500">Commissions paid</div>
                <div className="text-xl font-extrabold text-zinc-950">{fmt(detail.stats.paid_commission_usd)}</div>
              </div>
            </div>
            {payoutEligible.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm">
                  <strong>{payoutEligible.length} commission{payoutEligible.length !== 1 && "s"}</strong> pending — total <strong>{fmt(payoutAmount)}</strong>.
                </div>
                <button data-testid={`btn-payout-${p.promoter_id}`} onClick={recordPayout} disabled={savingPayout}
                  className="px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-full hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                  {savingPayout ? <Loader2 size={12} className="animate-spin" /> : <CircleDollarSign size={12} />} Record payout
                </button>
              </div>
            )}

            {traffic && (
              <div className="bg-white rounded-xl border border-zinc-200 p-4 mb-4" data-testid={`promoter-traffic-${p.promoter_id}`}>
                <div className="flex items-center gap-2 mb-3">
                  <ExternalLink size={14} className="text-zinc-600" />
                  <span className="font-semibold text-zinc-900 text-sm">Traffic & sources</span>
                  <span className="text-xs text-zinc-500 ml-auto">last {traffic.window_days}d · {traffic.totals.clicks_window} clicks · conv. {traffic.totals.conversion_rate_pct}%</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Top referrers</div>
                    {traffic.top_referrers.length === 0 ? (
                      <div className="text-xs text-zinc-400 italic">No clicks yet.</div>
                    ) : (
                      <div className="space-y-1.5" data-testid={`traffic-referrers-${p.promoter_id}`}>
                        {traffic.top_referrers.slice(0, 8).map((r) => {
                          const pct = traffic.totals.clicks_window
                            ? (r.clicks / traffic.totals.clicks_window) * 100 : 0;
                          return (
                            <div key={r.source} className="text-xs">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="font-mono text-zinc-700 truncate max-w-[200px]" title={r.source}>{r.source}</span>
                                <span className="text-zinc-500">{r.clicks} · {pct.toFixed(0)}%</span>
                              </div>
                              <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                <div className="h-full bg-zinc-900 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Top landing pages</div>
                    {traffic.top_landing.length === 0 ? (
                      <div className="text-xs text-zinc-400 italic">No data yet.</div>
                    ) : (
                      <div className="space-y-1.5" data-testid={`traffic-landing-${p.promoter_id}`}>
                        {traffic.top_landing.slice(0, 8).map((r) => {
                          const pct = traffic.totals.clicks_window
                            ? (r.clicks / traffic.totals.clicks_window) * 100 : 0;
                          return (
                            <div key={r.landing_path} className="text-xs">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="font-mono text-zinc-700 truncate max-w-[200px]" title={r.landing_path}>{r.landing_path}</span>
                                <span className="text-zinc-500">{r.clicks} · {pct.toFixed(0)}%</span>
                              </div>
                              <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                {traffic.daily.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5">Daily clicks (last {traffic.window_days}d)</div>
                    <div className="flex items-end gap-0.5 h-12" data-testid={`traffic-daily-${p.promoter_id}`}>
                      {(() => {
                        const max = Math.max(...traffic.daily.map((d) => d.clicks), 1);
                        return traffic.daily.map((d) => (
                          <div
                            key={d.day}
                            className="flex-1 bg-zinc-900 rounded-sm hover:bg-emerald-500 transition-colors"
                            style={{ height: `${(d.clicks / max) * 100}%` }}
                            title={`${d.day}: ${d.clicks}`}
                          />
                        ));
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl border border-zinc-200 overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-zinc-100 text-zinc-500">
                  {["Date", "Project", "Type", "Paid (USD)", "Rate", "Commission", "Status"].map(h => (
                    <th key={h} className="text-left px-3 py-2 font-semibold uppercase tracking-wide">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {detail.commissions.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-400">No commissions yet.</td></tr>
                  ) : detail.commissions.map(c => (
                    <tr key={c.commission_id} className="border-b border-zinc-50">
                      <td className="px-3 py-2 text-zinc-600">{new Date(c.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2 font-mono">{c.project_id}</td>
                      <td className="px-3 py-2">{c.payment_type}</td>
                      <td className="px-3 py-2">{fmt(c.paid_amount_usd)}</td>
                      <td className="px-3 py-2">{Math.round((c.rate || 0) * 100)}%</td>
                      <td className="px-3 py-2 font-bold text-emerald-700">{fmt(c.amount_usd)}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${c.status === "paid" ? "bg-emerald-100 text-emerald-700" : c.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminPromoters() {
  const navigate = useNavigate();
  const [promoters, setPromoters] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [tab, setTab] = useState(() => {
    if (typeof window === "undefined") return "active";
    const sp = new URLSearchParams(window.location.search);
    return sp.get("tab") === "applications" ? "applications" : "active";
  });
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        axiosAuth().get(`${API}/admin/promoters`),
        axiosAuth().get(`${API}/admin/promoter-applications`),
      ]);
      setPromoters(p.data || []);
      setApplications(a.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalPending = promoters.reduce((s, p) => s + (p.stats?.pending_commission_usd || 0), 0);
  const totalPaid = promoters.reduce((s, p) => s + (p.stats?.paid_commission_usd || 0), 0);
  const totalClicks = promoters.reduce((s, p) => s + (p.stats?.clicks || 0), 0);
  const pendingApps = applications.filter((a) => a.status === "pending");

  const exportCsv = async () => {
    try {
      const res = await axiosAuth().get(`${API}/admin/promoter-payouts/export.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "promoter_payouts.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Export failed"); }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-promoters-page">
      <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center gap-3">
        <Link to="/admin/dashboard" className="p-2 hover:bg-zinc-100 rounded-lg" data-testid="btn-back"><ArrowLeft size={16} /></Link>
        <BrandLogo variant="mark" size={32} />
        <h1 className="font-bold text-zinc-900">Promoter Program</h1>
        <div className="ml-auto flex gap-2">
          <Link to="/admin/promoter-materials" data-testid="link-materials" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 text-zinc-700 text-xs font-semibold rounded-full hover:bg-zinc-200">
            <FileText size={11} /> Guidelines & Materials
          </Link>
          <button onClick={exportCsv} data-testid="btn-export-csv" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-200 text-zinc-700 text-xs font-semibold rounded-full hover:bg-zinc-50">
            <Download size={11} /> Export CSV
          </button>
          <button onClick={() => setOpenCreate(true)} data-testid="btn-add-promoter" className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-zinc-950 text-white text-xs font-semibold rounded-full hover:bg-zinc-800">
            <Plus size={11} /> Add promoter
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <div className="bg-white border border-zinc-200 rounded-2xl p-4"><div className="text-xs text-zinc-500">Promoters</div><div className="text-2xl font-extrabold text-zinc-950">{promoters.length}</div></div>
          <div className="bg-white border border-zinc-200 rounded-2xl p-4"><div className="text-xs text-zinc-500">Total clicks</div><div className="text-2xl font-extrabold text-zinc-950">{totalClicks}</div></div>
          <div className="bg-white border border-zinc-200 rounded-2xl p-4"><div className="text-xs text-zinc-500">Pending payout</div><div className="text-2xl font-extrabold text-amber-600" data-testid="total-pending">{fmt(totalPending)}</div></div>
          <div className="bg-white border border-zinc-200 rounded-2xl p-4"><div className="text-xs text-zinc-500">Paid to date</div><div className="text-2xl font-extrabold text-emerald-700">{fmt(totalPaid)}</div></div>
          <button onClick={() => setTab("applications")} data-testid="card-pending-applications"
            className={`text-left bg-white border rounded-2xl p-4 transition-colors ${pendingApps.length > 0 ? "border-blue-300 hover:bg-blue-50" : "border-zinc-200"}`}>
            <div className="text-xs text-zinc-500">Pending applications</div>
            <div className={`text-2xl font-extrabold ${pendingApps.length > 0 ? "text-blue-600" : "text-zinc-400"}`}>{pendingApps.length}</div>
          </button>
        </div>

        <div className="flex gap-1 border-b border-zinc-200 mb-4">
          <button onClick={() => setTab("active")} data-testid="tab-active"
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "active" ? "text-zinc-950 border-zinc-950" : "text-zinc-500 border-transparent hover:text-zinc-700"}`}>
            Active promoters
          </button>
          <button onClick={() => setTab("applications")} data-testid="tab-applications"
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "applications" ? "text-zinc-950 border-zinc-950" : "text-zinc-500 border-transparent hover:text-zinc-700"}`}>
            Applications {pendingApps.length > 0 && <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{pendingApps.length}</span>}
          </button>
        </div>

        {tab === "active" ? (
          <div className="bg-white border border-zinc-200 rounded-2xl overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-zinc-100">
                {["Promoter", "Tracking link", "Rate", "Clicks", "Projects", "Pending", "Status", ""].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">{h}</th>
                ))}
              </tr></thead>
              <tbody data-testid="promoters-table">
                {loading ? <tr><td colSpan={8} className="px-5 py-12 text-center"><Loader2 size={20} className="animate-spin text-zinc-400 mx-auto" /></td></tr>
                  : promoters.length === 0 ? <tr><td colSpan={8} className="px-5 py-12 text-center text-zinc-400">No promoters yet — add your first above or approve an application.</td></tr>
                  : promoters.map(p => <PromoterRow key={p.promoter_id} p={p} onUpdated={load} origin={origin} />)
                }
              </tbody>
            </table>
          </div>
        ) : (
          <ApplicationsList rows={applications} loading={loading} onChange={load} />
        )}
      </div>

      <CreatePromoterModal open={openCreate} onClose={() => setOpenCreate(false)} onCreated={load} />
    </div>
  );
}

function ApplicationsList({ rows, loading, onChange }) {
  const [acting, setActing] = useState(null);

  const approve = async (a) => {
    const code = window.prompt(`Approve "${a.name}"? (Optional) custom tracking code (leave blank for auto):`, "");
    if (code === null) return;
    setActing(a.application_id);
    try {
      await axiosAuth().post(`${API}/admin/promoter-applications/${a.application_id}/approve`, {
        code: code.trim() || undefined,
      });
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || "Approve failed"); }
    finally { setActing(null); }
  };
  const reject = async (a) => {
    const reason = window.prompt(`Reject "${a.name}"? Optional reason emailed to applicant:`, "");
    if (reason === null) return;
    setActing(a.application_id);
    try {
      await axiosAuth().post(`${API}/admin/promoter-applications/${a.application_id}/reject`, {
        reason: reason || undefined,
      });
      onChange?.();
    } catch (e) { alert(e.response?.data?.detail || "Reject failed"); }
    finally { setActing(null); }
  };

  if (loading) return <div className="py-12 flex justify-center"><Loader2 size={20} className="animate-spin text-zinc-400" /></div>;
  if (rows.length === 0) return <div className="py-12 text-center text-zinc-400 bg-white border border-zinc-200 rounded-2xl">No applications yet.</div>;

  return (
    <div className="space-y-3" data-testid="applications-list">
      {rows.map((a) => (
        <div key={a.application_id} data-testid={`application-${a.application_id}`}
          className={`bg-white border rounded-2xl p-5 ${
            a.status === "approved" ? "border-emerald-200" : a.status === "rejected" ? "border-zinc-200 opacity-75" : "border-blue-200"
          }`}>
          <div className="flex items-start gap-3 flex-wrap mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-bold text-zinc-950 text-sm">{a.name}</span>
                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  a.status === "approved" ? "bg-emerald-600 text-white" :
                  a.status === "rejected" ? "bg-zinc-400 text-white" : "bg-blue-500 text-white"
                }`}>{a.status}</span>
              </div>
              <div className="text-xs text-zinc-500">{a.email} · applied {new Date(a.created_at).toLocaleDateString()}</div>
            </div>
            {a.status === "pending" && (
              <div className="flex gap-2">
                <button data-testid={`btn-approve-app-${a.application_id}`} onClick={() => approve(a)} disabled={acting === a.application_id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-full hover:bg-emerald-700 disabled:opacity-50">
                  {acting === a.application_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />} Approve
                </button>
                <button data-testid={`btn-reject-app-${a.application_id}`} onClick={() => reject(a)} disabled={acting === a.application_id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-zinc-200 text-zinc-700 text-xs font-semibold rounded-full hover:bg-zinc-50 disabled:opacity-50">
                  <X size={12} /> Reject
                </button>
              </div>
            )}
          </div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1">Audience</div>
          <p className="text-sm text-zinc-700 whitespace-pre-wrap">{a.audience_description}</p>
          {a.primary_channels?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {a.primary_channels.map((c) => <span key={c} className="text-[10px] font-semibold uppercase tracking-wider bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full">{c}</span>)}
            </div>
          )}
          {a.sample_links?.length > 0 && (
            <ul className="mt-3 text-xs space-y-1">
              {a.sample_links.map((l, i) => <li key={i}><a href={l} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{l}</a></li>)}
            </ul>
          )}
          {a.monthly_reach && <div className="mt-3 text-xs text-zinc-600"><strong>Monthly reach:</strong> {a.monthly_reach}</div>}
          {a.pitch && (
            <div className="mt-3 bg-zinc-50 border border-zinc-200 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1">Pitch</div>
              <p className="text-xs text-zinc-700 whitespace-pre-wrap">{a.pitch}</p>
            </div>
          )}
          {a.status === "rejected" && a.reject_reason && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-red-700 mb-1">Reject reason</div>
              <p className="text-xs text-red-800 whitespace-pre-wrap">{a.reject_reason}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
